'use client'

import { Button, Center, Spinner, Stack, Text } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from '@tanstack/react-router'
import { ssoApi } from '@/entities/auth/api'
import { jwtDecode } from 'jwt-decode'
import {
  clearPkceVerifier,
  getPkceVerifier,
} from '@/features/auth/model/sso-pkce'
import { toaster } from '@/shared/ui'
import { config, useAuthStore } from '@/shared'
import type { IUser } from '@/shared/store/authStore'

export function AuthSsoCallbackPage() {
  const { t } = useTranslation()
  const router = useRouter()
  const [message, setMessage] = useState(() =>
    t('auth.callback.completing'),
  )
  const { setUser } = useAuthStore()

  useEffect(() => {
    const completeSso = async () => {
      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')
      const verifier = getPkceVerifier()

      if (!code || !verifier) {
        setMessage(t('auth.callback.unableComplete'))
        return
      }

      try {
        const data = await ssoApi.callback({
          code,
          code_verifier: verifier,
          redirect_uri: config.SSO_REDIRECT_URI,
        })

        const accessToken = data.accessToken ?? data.access_token
        const refreshToken = data.refreshToken ?? data.refresh_token
        const user = jwtDecode<IUser>(accessToken ?? '{}')
        setUser(user)

        if (!accessToken) {
          throw new Error('Missing access token')
        }

        localStorage.setItem('access_token', accessToken)
        if (refreshToken) {
          localStorage.setItem('refresh_token', refreshToken)
        }

        clearPkceVerifier()
        toaster.success({ title: t('auth.callback.signedInTitle') })
        router.navigate({ to: '/' })
      } catch {
        setMessage(t('auth.callback.ssoFailed'))
        toaster.error({ title: t('auth.callback.ssoErrorTitle') })
      }
    }

    void completeSso()
  }, [router, t])

  return (
    <Center minH="100vh" bg="bg" px={4}>
      <Stack align="center" gap={4}>
        <Spinner color="primary.fg" size="lg" />
        <Text color="fg.muted" fontSize="sm" textAlign="center">
          {message}
        </Text>
        <Button onClick={() => router.navigate({ to: '/auth/entry' })}>
          {t('auth.callback.retry')}
        </Button>
      </Stack>
    </Center>
  )
}
